import { Button, Center, CopyButton, Group, Loader, Modal, Stack, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconBrandLinkedin, IconBrandWhatsapp, IconCheck, IconCopy, IconDownload } from "@tabler/icons-react";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "../context/useAuth";
import type { IntakeLink } from "../types/api";
import { axioTokens } from "../theme";
import {
  INTAKE_CHANNEL_INSTAGRAM,
  INTAKE_CHANNEL_LABELS,
  INTAKE_CHANNEL_LINKEDIN,
  INTAKE_CHANNEL_WHATSAPP,
  JOINED_PER_CHANNEL_LABEL,
  SHARE_CHANNEL_INSTAGRAM,
  SHARE_CHANNEL_LINKEDIN,
  SHARE_CHANNEL_WHATSAPP,
  SHARE_COPY_STORY_LABEL,
  SHARE_DOWNLOAD_QR_LABEL,
} from "../constants/matching";
import { trackEvent } from "../lib/observability";

interface Props {
  opened: boolean;
  onClose: () => void;
  link: IntakeLink | null;
  loading?: boolean;
}

function applyUrl(token: string, channel?: string): string {
  const url = `${window.location.origin}/apply/${token}`;
  return channel ? `${url}?ch=${channel}` : url;
}

function downloadQr(filename: string) {
  const svg = document.getElementById("intake-share-qr");
  if (!(svg instanceof SVGSVGElement)) return;
  const blob = new Blob([svg.outerHTML], { type: "image/svg+xml" });
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(href);
}

export function ShareIntakeLinkModal({ opened, onClose, link, loading }: Props) {
  const { user } = useAuth();
  const url = link ? applyUrl(link.token) : "";
  const waUrl = link ? applyUrl(link.token, INTAKE_CHANNEL_WHATSAPP) : "";
  const liUrl = link ? applyUrl(link.token, INTAKE_CHANNEL_LINKEDIN) : "";
  const igUrl = link ? applyUrl(link.token, INTAKE_CHANNEL_INSTAGRAM) : "";
  const whatsappText = link
    ? encodeURIComponent(`Join my talent pool on AxioHire: ${waUrl}`)
    : "";
  const linkedInShare = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(liUrl)}`;
  const shareHint =
    user?.role === "affiliate"
      ? "Candidates can open this link on their phone, pick a job they are interested in, and join the AxioHire pool. You earn a sourcing fee when they are placed."
      : "Candidates can open this link on their phone, pick a job they are interested in, and join your talent pool. You can submit them to roles later from My Candidates.";
  const channelCounts = link?.joined_by_channel ?? {};

  return (
    <Modal opened={opened} onClose={onClose} title="Share candidate link" centered size="sm">
      {loading || !link ? (
        <Center py="xl">
          <Loader color="gray" size="sm" />
        </Center>
      ) : (
      <Stack gap="md" align="center">
        <Text size="sm" c="dimmed" ta="center">
          {shareHint}
        </Text>

        <QRCodeSVG
          id="intake-share-qr"
          value={igUrl || url}
          size={axioTokens.unit[180]}
          level="M"
          includeMargin
          title="Candidate application QR code"
        />

        <Text size="xs" c="dimmed" ta="center" style={{ wordBreak: "break-all" }}>
          {url}
        </Text>

        <Group w="100%" grow>
          <CopyButton value={url}>
            {({ copied, copy }) => (
              <Button
                variant={copied ? "filled" : "default"}
                color={copied ? "green" : undefined}
                leftSection={copied ? <IconCheck size="var(--axio-unit-16)" /> : <IconCopy size="var(--axio-unit-16)" />}
                onClick={() => {
                  copy();
                  notifications.show({
                    color: "green",
                    title: "Link copied",
                    message: "Candidate link copied to clipboard.",
                  });
                }}
              >
                {copied ? "Copied" : "Copy link"}
              </Button>
            )}
          </CopyButton>
        </Group>

        <Group w="100%" grow>
          <Button
            component="a"
            href={`https://wa.me/?text=${whatsappText}`}
            target="_blank"
            rel="noopener noreferrer"
            color="green"
            leftSection={<IconBrandWhatsapp size="var(--axio-unit-16)" />}
            onClick={() => trackEvent("intake.share", { channel: INTAKE_CHANNEL_WHATSAPP })}
          >
            {SHARE_CHANNEL_WHATSAPP}
          </Button>
          <Button
            component="a"
            href={linkedInShare}
            target="_blank"
            rel="noopener noreferrer"
            leftSection={<IconBrandLinkedin size="var(--axio-unit-16)" />}
            onClick={() => trackEvent("intake.share", { channel: INTAKE_CHANNEL_LINKEDIN })}
          >
            {SHARE_CHANNEL_LINKEDIN}
          </Button>
        </Group>
        <Group w="100%" grow>
          <CopyButton value={igUrl}>
            {({ copied, copy }) => (
              <Button
                variant="default"
                onClick={() => {
                  copy();
                  trackEvent("intake.share", { channel: INTAKE_CHANNEL_INSTAGRAM });
                  notifications.show({
                    color: "green",
                    title: SHARE_CHANNEL_INSTAGRAM,
                    message: SHARE_COPY_STORY_LABEL,
                  });
                }}
              >
                {copied ? "Copied" : SHARE_COPY_STORY_LABEL}
              </Button>
            )}
          </CopyButton>
          <Button
            variant="default"
            leftSection={<IconDownload size="var(--axio-unit-16)" />}
            onClick={() => downloadQr("axiohire-intake-qr.svg")}
          >
            {SHARE_DOWNLOAD_QR_LABEL}
          </Button>
        </Group>

        {link.intake_count > 0 && (
          <Text size="xs" c="dimmed">
            {link.intake_count} candidate{link.intake_count === 1 ? "" : "s"} joined via this link
          </Text>
        )}
        {Object.keys(channelCounts).length > 0 ? (
          <Text size="xs" c="dimmed" ta="center">
            {JOINED_PER_CHANNEL_LABEL}:{" "}
            {Object.entries(channelCounts)
              .map(([channel, count]) => `${INTAKE_CHANNEL_LABELS[channel] ?? channel} ${count}`)
              .join(" · ")}
          </Text>
        ) : null}
      </Stack>
      )}
    </Modal>
  );
}
